import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { barberService } from '../../services/barberService';
import { serviceTypeService } from '../../services/serviceTypeService';
import { Barber, ProfileUpdateRequest, ServiceType, BarberServiceRequest, BarberOfferedService } from '../../types/barber.types';
import { useToast } from '../../context/ToastContext';
import ImageUpload from '../../components/common/ImageUpload';
import AdminHeader from '../../components/admin/AdminHeader';

const EditBarberPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { showToast } = useToast();

    const [barber, setBarber] = useState<Barber | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const [formData, setFormData] = useState({
        address: '',
        whatsapp: '',
        instagramUrl: '',
        facebookUrl: '',
        profilePictureUrl: '',
        isIndependent: false
    });

    const [serviceTypes, setServiceTypes] = useState<ServiceType[]>([]);
    const [selectedServiceIds, setSelectedServiceIds] = useState<number[]>([]);

    useEffect(() => {
        loadBarber();
    }, [id]);

    const loadBarber = async () => {
        try {
            setLoading(true);
            const [data, typesData] = await Promise.all([
                barberService.getBarberById(Number(id)),
                serviceTypeService.getAllServices()
            ]);
            
            setBarber(data);
            setServiceTypes(typesData);
            
            // Rehydrate selected services
            if (data.services) {
                setSelectedServiceIds(data.services.map((s: BarberOfferedService) => s.serviceTypeId));
            }

            setFormData({
                address: data.address || '',
                whatsapp: data.whatsapp || '',
                instagramUrl: data.instagramUrl || '',
                facebookUrl: data.facebookUrl || '',
                profilePictureUrl: data.profilePicture || '',
                isIndependent: data.isIndependent || false
            });
        } catch (error: any) {
            console.error('Error loading barber:', error);
            showToast('Error al cargar el barbero', 'error');
            navigate('/admin/barbers');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        try {
            setSaving(true);

            const request: ProfileUpdateRequest = {
                address: formData.address || undefined,
                whatsapp: formData.whatsapp || undefined,
                instagramUrl: formData.instagramUrl || undefined,
                facebookUrl: formData.facebookUrl || undefined,
                profilePictureUrl: formData.profilePictureUrl || undefined,
                isIndependent: formData.isIndependent
            };

            const servicesRequest: BarberServiceRequest[] = selectedServiceIds.map(typeId => ({
                serviceTypeId: typeId
            }));

            await Promise.all([
                barberService.updateBarberProfile(Number(id), request),
                barberService.updateBarberServices(Number(id), servicesRequest)
            ]);
            
            showToast('Perfil actualizado correctamente', 'success');
            navigate(`/admin/barbers/${id}/view`);
        } catch (error: any) {
            console.error('Error updating barber:', error);
            showToast(error.response?.data?.message || 'Error al actualizar el perfil', 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleImageUploaded = (url: string) => {
        setFormData(prev => ({ ...prev, profilePictureUrl: url }));
    };

    const handleServiceToggle = (serviceTypeId: number) => {
        setSelectedServiceIds(prev => 
            prev.includes(serviceTypeId) 
                ? prev.filter(id => id !== serviceTypeId)
                : [...prev, serviceTypeId]
        );
    };

    if (loading) {
        return (
            <div className="container mx-auto p-xl">
                <div className="card p-xl text-center">
                    <div className="spinner m-auto mb-md"></div>
                    <p>Cargando...</p>
                </div>
            </div>
        );
    }

    if (!barber) {
        return (
            <div className="container mx-auto p-xl">
                <div className="card p-xl text-center">
                    <p className="text-error">No se pudo cargar el barbero</p>
                    <button className="btn btn-primary mt-md" onClick={() => navigate('/admin/barbers')}>
                        Volver al listado
                    </button>
                </div>
            </div>
        );
    }

    return (
        <>
            <AdminHeader
                title={`Editar: ${barber.firstName} ${barber.lastName}`}
                backPath={`/admin/barbers/${id}/view`}
            />
            <div className="container mx-auto p-md md:p-xl" style={{ maxWidth: '900px' }}>
                <p className="mb-lg" style={{ color: '#f59e0b', fontWeight: '600' }}>"{barber.nickname}"</p>

                {/* Form Card */}
                <form onSubmit={handleSubmit}>
                    <div className="card p-md md:p-xl" style={{ background: '#111111', borderRadius: '1rem', border: '1px solid #2a2a2a', boxShadow: '0 8px 32px rgba(0,0,0,0.5)' }}>
                        {/* Profile Picture */}
                        <div className="mb-xl pb-xl" style={{ borderBottom: '1px solid #2a2a2a' }}>
                            <h2 className="font-bold mb-md" style={{ fontSize: 'clamp(1.125rem, 3vw, 1.25rem)', color: '#f59e0b' }}>
                                📷 Foto de Perfil
                            </h2>
                            <ImageUpload
                                currentImageUrl={formData.profilePictureUrl}
                                onImageUploaded={handleImageUploaded}
                            />
                        </div>

                        {/* Professional Config */}
                        <div className="mb-xl pb-xl" style={{ borderBottom: '1px solid #2a2a2a' }}>
                            <h2 className="font-bold mb-md" style={{ fontSize: 'clamp(1.125rem, 3vw, 1.25rem)', color: '#f59e0b' }}>
                                ⚙️ Configuración
                            </h2>
                            <div className="flex items-center gap-sm">
                                <input
                                    type="checkbox"
                                    checked={formData.isIndependent}
                                    onChange={(e) => setFormData({ ...formData, isIndependent: e.target.checked })}
                                    disabled={saving}
                                    className="w-5 h-5 accent-amber-500 rounded text-amber-500 focus:ring-amber-500 bg-gray-900 border-gray-700"
                                    id="isIndependent"
                                />
                                <label htmlFor="isIndependent" className="form-label mb-0 cursor-pointer" style={{ color: '#e5e5e5' }}>
                                    Barbero Independiente (no staff)
                                </label>
                            </div>
                        </div>

                        {/* Contact Information */}
                        <div className="mb-xl pb-xl" style={{ borderBottom: '1px solid #2a2a2a' }}>
                            <h2 className="font-bold mb-md" style={{ fontSize: 'clamp(1.125rem, 3vw, 1.25rem)', color: '#f59e0b' }}>
                                📞 Información de Contacto
                            </h2>

                            <div className="space-y-md">
                                <div>
                                    <label className="form-label" style={{ color: '#a3a3a3' }}>
                                        📍 Dirección
                                    </label>
                                    <input
                                        type="text"
                                        className="form-input"
                                        style={{ background: '#1a1a1a', border: '1px solid #333', color: '#e5e5e5' }}
                                        placeholder="Ej: Av. Principal 123, Ciudad"
                                        value={formData.address}
                                        onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                                        disabled={saving}
                                    />
                                </div>

                                <div>
                                    <label className="form-label" style={{ color: '#a3a3a3' }}>
                                        📱 WhatsApp
                                    </label>
                                    <input
                                        type="tel"
                                        className="form-input"
                                        style={{ background: '#1a1a1a', border: '1px solid #333', color: '#e5e5e5' }}
                                        placeholder="Ej: 3543455433"
                                        value={formData.whatsapp}
                                        onChange={(e) => setFormData({ ...formData, whatsapp: e.target.value })}
                                        disabled={saving}
                                    />
                                    <p className="text-xs mt-xs" style={{ color: '#737373' }}>
                                        Solo números, sin espacios ni guiones
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Social Media */}
                        <div className="mb-xl pb-xl" style={{ borderBottom: '1px solid #2a2a2a' }}>
                            <h2 className="font-bold mb-md" style={{ fontSize: 'clamp(1.125rem, 3vw, 1.25rem)', color: '#f59e0b' }}>
                                🌐 Redes Sociales
                            </h2>

                            <div className="space-y-md">
                                <div>
                                    <label className="form-label" style={{ color: '#a3a3a3' }}>
                                        📸 Instagram URL
                                    </label>
                                    <input
                                        type="url"
                                        className="form-input"
                                        style={{ background: '#1a1a1a', border: '1px solid #333', color: '#e5e5e5' }}
                                        placeholder="https://instagram.com/usuario"
                                        value={formData.instagramUrl}
                                        onChange={(e) => setFormData({ ...formData, instagramUrl: e.target.value })}
                                        disabled={saving}
                                    />
                                </div>

                                <div>
                                    <label className="form-label" style={{ color: '#a3a3a3' }}>
                                        📘 Facebook URL
                                    </label>
                                    <input
                                        type="url"
                                        className="form-input"
                                        style={{ background: '#1a1a1a', border: '1px solid #333', color: '#e5e5e5' }}
                                        placeholder="https://facebook.com/usuario"
                                        value={formData.facebookUrl}
                                        onChange={(e) => setFormData({ ...formData, facebookUrl: e.target.value })}
                                        disabled={saving}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Services */}
                        <div className="mb-xl">
                            <h2 className="font-bold mb-md" style={{ fontSize: 'clamp(1.125rem, 3vw, 1.25rem)', color: '#f59e0b' }}>
                                💈 Servicios Ofrecidos
                            </h2>
                            <p className="text-sm mb-md" style={{ color: '#a3a3a3' }}>
                                Seleccioná los servicios que este barbero va a ofrecer.
                            </p>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-sm">
                                {serviceTypes.map(service => (
                                    <label 
                                        key={service.id} 
                                        className="flex items-center gap-sm p-sm rounded-lg cursor-pointer transition-colors"
                                        style={{ 
                                            background: selectedServiceIds.includes(service.id) ? 'rgba(245, 158, 11, 0.1)' : '#1a1a1a',
                                            border: `1px solid ${selectedServiceIds.includes(service.id) ? '#f59e0b' : '#333'}` 
                                        }}
                                    >
                                        <input 
                                            type="checkbox" 
                                            checked={selectedServiceIds.includes(service.id)}
                                            onChange={() => handleServiceToggle(service.id)}
                                            disabled={saving}
                                            className="w-5 h-5 accent-amber-500 rounded text-amber-500 focus:ring-amber-500 bg-gray-900 border-gray-700"
                                        />
                                        <div className="flex flex-col">
                                            <span style={{ color: selectedServiceIds.includes(service.id) ? '#f59e0b' : '#e5e5e5', fontWeight: '500' }}>
                                                {service.name}
                                            </span>
                                            <span style={{ color: '#737373', fontSize: '0.8rem' }}>
                                                {service.defaultDurationMinutes} min
                                            </span>
                                        </div>
                                    </label>
                                ))}
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '1rem',
                            paddingTop: '2rem',
                            borderTop: '1px solid #2a2a2a'
                        }} className="pt-lg">
                            <button
                                type="button"
                                className="btn"
                                style={{ width: '100%', background: 'transparent', border: '1px solid #404040', color: '#a3a3a3', borderRadius: '0.75rem', padding: '0.875rem', fontWeight: '600' }}
                                onClick={() => navigate(`/admin/barbers/${id}/view`)}
                                disabled={saving}
                            >
                                Cancelar
                            </button>
                            <button
                                type="submit"
                                className="btn"
                                style={{ width: '100%', background: 'linear-gradient(135deg, #f59e0b, #d97706)', border: 'none', color: '#000', borderRadius: '0.75rem', padding: '0.875rem', fontWeight: '700', fontSize: '1rem', boxShadow: '0 4px 15px rgba(245, 158, 11, 0.3)' }}
                                disabled={saving}
                            >
                                {saving ? '💾 Guardando...' : '💾 Guardar Cambios'}
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </>
    );
};

export default EditBarberPage;
